import { useCallback, useEffect, useState } from "react";
import { MessageDescriptor, useIntl } from "react-intl";
import { useBlocker } from "react-router-dom";
import { msg_confirm_cancel } from "@dvrt/graphql-widgets";

export function usePageTitle(title: MessageDescriptor) {
    const { formatMessage } = useIntl();

    useEffect(() => {
        const oldTitle = document.title;
        document.title =
            formatMessage({ id: "title.site", defaultMessage: "Conference" }) + " - " + formatMessage(title);
        return () => {
            document.title = oldTitle;
        };
    });
}

export const useWindowEvent = <K extends keyof WindowEventMap>(
    event: K,
    listener: (this: Window, ev: WindowEventMap[K]) => any,
) => {
    const callback = useCallback(listener, [event]);

    useEffect(() => {
        window.addEventListener(event, callback);
        return () => window.removeEventListener(event, callback);
    }, [event]);
};

const getWindowSize = () => ({
    width: window.innerWidth,
    height: window.innerHeight,
});

export const useWindowSize = () => {
    const [size, setSize] = useState(getWindowSize());

    useWindowEvent("resize", () => setSize(getWindowSize()));

    return size;
};

function disableBeforeUnload() {
    window.onbeforeunload = null;
}

export function useFormGuard(enabled: boolean): void {
    const { formatMessage } = useIntl();

    const blocker = useBlocker(
        ({ currentLocation, nextLocation }) => enabled && currentLocation.pathname !== nextLocation.pathname,
    );

    if (enabled) window.onbeforeunload = () => "";
    else disableBeforeUnload();

    useEffect(() => {
        if (blocker.state == "blocked") {
            if (!window.confirm(formatMessage(msg_confirm_cancel))) {
                disableBeforeUnload();
                blocker.reset();
            } else blocker.proceed();
        }
    }, [blocker.state, formatMessage]);
}
